import type { MedplumClient } from "@medplum/core";
import type { Organization, Questionnaire, QuestionnaireItem, Reference } from "@medplum/fhirtypes";
import {
  type CreateResourceOptions,
  FhirResource,
  type NullableExtension,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData } from "@vibrant/models/fhir/fhir-resource-types";
import { isMedplumResourceMissingError } from "@/lib/errors/is-medplum-resource-missing-error";
import { createLogger } from "@/lib/logger";
import { parseTags, tagsToString } from "@/lib/utils/questionnaires/tags";
import { Question } from "./Question";

const logger = createLogger("Survey");

export interface SurveyCreateRequest {
  practiceId: string;
  title: string;
  description?: string;
  identifier?: string;
  status?: "draft" | "active" | "retired";
  item?: QuestionnaireItem[];
  /**
   * Optional source tag to annotate the survey origin.
   * When set to "ai", the survey will be created with a useContext entry
   * `{ code: { system: Survey.UseContext.Source, code: "ai", display: "AI" } }`.
   */
  source?: "ai";
}

export interface SurveyUpdateRequest {
  title?: string;
  description?: string;
  status?: "draft" | "active" | "retired";
  item?: QuestionnaireItem[];
  startedBlank?: boolean;
  tags?: string[];
}

export interface SurveySearchOptions {
  practiceId: string;
  page?: number;
  size?: number;
  sort?: string;
  filter?: string;
  /**
   * When true, include AI-parsed surveys in results.
   * Defaults to false so AI-parsed surveys are excluded from UI listings.
   */
  includeAi?: boolean;
}

export interface SurveySearchResult {
  surveys: Survey[];
  total: number;
}

interface CreateSurveyFromTemplateParams {
  templateId: string;
  practiceId: string;
  title?: string;
  status?: "draft" | "active";
  createOptions?: CreateResourceOptions;
}

/**
 * Survey class represents questionnaires used within a practice for collecting patient data.
 * It's based on the FHIR Questionnaire resource and provides methods to manage surveys
 * that are always linked to a specific Practice.
 */
export class Survey extends FhirResource<Questionnaire> {
  static readonly ResourceType = "Questionnaire";

  // Cached tags to prevent creating new arrays on every access.
  // This is important for React components using Zustand selectors - returning
  // a new array reference on every access causes infinite re-render loops.
  private _cachedTags: string[] | undefined;

  static readonly UseContext = {
    /**
     * Used to identify questionnaires that are surveys.
     */
    Survey: "vibrant.survey",
    /**
     * Used to denote the source of a survey (e.g., AI).
     */
    Source: "vibrant.survey.source",
  } as const;

  static readonly Identifier = {
    /**
     * System for survey identifiers.
     * @deprecated Used for legacy intake survey.
     */
    Vibrant: "Vibrant",
  } as const;

  static readonly Extension = {
    /**
     * Indicates that the survey was started from a blank template.
     */
    StartedBlank: "vibrant.survey.started-blank",
    /**
     * The template ID this questionnaire was copied from.
     */
    CopiedFromTemplateId: "vibrant.questionnaire.copied-from-template-id",
    /**
     * User-defined tags for organizing and categorizing surveys.
     * Stored as a comma-separated string value.
     */
    Tags: "vibrant.survey.tags",
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                     VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------

  /**
   * Returns the title of the survey
   */
  get title(): string | undefined {
    return this.resource.title;
  }

  /**
   * Returns the description of the survey
   */
  get description(): string | undefined {
    return this.resource.description;
  }

  get isPublished(): boolean {
    return this.status === "active";
  }

  /**
   * Returns the status of the survey
   */
  get status(): "draft" | "active" | "retired" | "unknown" | undefined {
    return this.resource.status;
  }

  /**
   * Get all questions as wrapped Question objects
   */
  get questions(): Question[] {
    return (this.resource.item ?? []).map((item) => Question.fromFHIR(item));
  }

  /**
   * Get raw FHIR questionnaire items
   * @deprecated Use questions property instead
   */
  get questionItems(): QuestionnaireItem[] {
    return this.resource.item ?? [];
  }

  /**
   * Returns the practice reference resolved from the survey context
   */
  get practice(): Reference<Organization> | undefined {
    const practiceContext = this.resource.useContext?.find(
      (context) =>
        context.code?.system === Survey.UseContext.Survey &&
        context.code?.code?.startsWith("Organization/"),
    );
    const practiceReference =
      practiceContext?.valueReference?.reference ?? practiceContext?.code?.code;

    return practiceReference ? { reference: practiceReference } : undefined;
  }

  /**
   * Returns whether the survey has been started (has items or was started from blank)
   */
  get started(): boolean {
    const hasItems = this.items.length > 0;
    const hasStartedBlankExtension = this.getExtension(Survey.Extension.StartedBlank) !== undefined;
    return hasItems || hasStartedBlankExtension;
  }

  /**
   * Returns the survey identifier
   */
  get identifier(): string | undefined {
    return this.resource.identifier?.find((id) => id.system === Survey.Identifier.Vibrant)?.value;
  }

  /**
   * Returns the last updated timestamp of the survey
   */
  get lastUpdated(): string | undefined {
    return this.resource.meta?.lastUpdated;
  }

  /**
   * Returns the version of the survey
   */
  get version(): string | undefined {
    return this.resource.meta?.versionId;
  }

  /**
   * Returns all items (questions) in the survey
   */
  get items(): QuestionnaireItem[] {
    return this.resource.item ?? [];
  }

  /**
   * Returns the number of items in the survey
   */
  get itemCount(): number {
    return this.items.length;
  }

  /**
   * Returns an item by its linkId
   */
  getItemByLinkId(linkId: string): QuestionnaireItem | undefined {
    return this.findItemRecursive(this.items, linkId);
  }

  /**
   * Returns all items of a specific type
   */
  getItemsByType(type: QuestionnaireItem["type"]): QuestionnaireItem[] {
    return this.filterItemsRecursive(this.items, (item) => item.type === type);
  }

  /**
   * Returns all required items
   */
  getRequiredItems(): QuestionnaireItem[] {
    return this.filterItemsRecursive(this.items, (item) => item.required === true);
  }

  /**
   * Get the template ID this survey was copied from
   */
  get copiedFromTemplateId(): string | undefined {
    return this.getExtension(Survey.Extension.CopiedFromTemplateId)?.valueString;
  }

  /**
   * Get the user-defined tags for this survey.
   * Tags are stored as a comma-separated string in the extension.
   *
   * Note: Returns a cached array to ensure stable references for React components.
   * This prevents infinite re-render loops in Zustand selectors that use shallow comparison.
   */
  get tags(): string[] {
    if (this._cachedTags === undefined) {
      this._cachedTags = parseTags(this.getExtension(Survey.Extension.Tags)?.valueString);
    }
    return this._cachedTags;
  }

  //------------------------------------------------------------------------------------------------
  //                                       CLASS METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Creates a new survey.
   *
   * @param medplum - The Medplum client.
   * @param data - The survey data.
   * @returns The created survey.
   */
  static async create(
    medplum: MedplumClient,
    data: SurveyCreateRequest,
    options?: CreateResourceOptions,
  ): Promise<Survey> {
    const resource = await Survey.createResource(medplum, Survey.toFhirCreateObject(data), options);
    return new Survey(resource);
  }

  /**
   * Duplicates an existing survey.
   * Creates a new survey with the same content but a new ID and appends " (Copy)" to the title.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the survey to duplicate.
   * @returns The newly created duplicate survey.
   */
  static async duplicate(
    medplum: MedplumClient,
    id: string,
    options?: CreateResourceOptions,
  ): Promise<Survey> {
    // Fetch the original survey
    const original = await medplum.readResource("Questionnaire", id);

    // Prepare the duplicate data by extracting necessary fields
    const duplicateData: ResourceData<Questionnaire> = {
      title: `${original.title || "Untitled"} (Copy)`,
      description: original.description,
      status: "draft", // Always set duplicates to draft status
      useContext: original.useContext,
      item: original.item ? JSON.parse(JSON.stringify(original.item)) : undefined, // Deep clone items
      extension: original.extension,
    };

    // Create the duplicate survey
    const resource = await Survey.createResource(medplum, duplicateData, options);
    return new Survey(resource);
  }

  /**
   * Gets a survey by ID.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the survey.
   * @returns The survey or undefined if not found.
   */
  static async getById(medplum: MedplumClient, id: string): Promise<Survey | undefined> {
    try {
      const resource = await medplum.readResource("Questionnaire", id);
      // Verify it's actually a survey by checking the useContext
      const isSurvey = resource.useContext?.some(
        (context) =>
          context.code?.system === Survey.UseContext.Survey &&
          context.code?.code?.startsWith("Organization/"),
      );
      return isSurvey ? new Survey(resource) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Searches for surveys and returns an array of Survey instances.
   * This method is compatible with the useModelSearchSubscription hook.
   *
   * @param medplum - The Medplum client.
   * @param options - Search options including pagination, sorting, and filtering.
   * @returns An array of Survey instances.
   */
  static async search(
    medplum: MedplumClient,
    query: SurveySearchOptions,
    options?: SearchOptions,
  ): Promise<Survey[]> {
    // When SearchOptions are provided (e.g. from the hook's pagination prop), merge them
    // into the query so searchWithTotal handles everything in one path.
    const mergedQuery: SurveySearchOptions = options
      ? {
          ...query,
          size: options.limit ?? query.size ?? 10,
          page:
            options.offset !== undefined
              ? Math.floor(options.offset / (options.limit ?? query.size ?? 10)) + 1
              : query.page,
        }
      : query;

    const result = await this.searchWithTotal(medplum, mergedQuery);
    return result.surveys;
  }

  /**
   * Searches for surveys with advanced options including pagination, filtering, and sorting.
   * This method ensures only actual surveys are returned (filtered by Survey.Code.Survey).
   *
   * @param medplum - The Medplum client.
   * @param options - Search options including pagination, sorting, and filtering.
   * @returns The search results with surveys and total count.
   */
  static async searchWithTotal(
    medplum: MedplumClient,
    options: SurveySearchOptions,
  ): Promise<SurveySearchResult> {
    // Execute the search
    const bundle = await medplum.search("Questionnaire", Survey.toFhirSearchQuery(options));

    // Convert bundle entries to Survey instances
    const surveys =
      bundle.entry
        ?.map((entry) => entry.resource)
        .filter(
          (resource): resource is NonNullable<typeof resource> =>
            resource?.resourceType === "Questionnaire",
        )
        .map((questionnaire) => new Survey(questionnaire)) || [];

    return {
      surveys,
      total: bundle.total ?? 0,
    };
  }

  /**
   * Search for a survey by slug.
   *
   * @deprecated Used for legacy intake survey.
   *
   * @param medplum - The Medplum client.
   * @param slug - The slug of the survey.
   *
   * @returns The survey or undefined if not found.
   */
  static async searchBySlug(medplum: MedplumClient, slug: string): Promise<Survey | undefined> {
    const questionnaire = await medplum.searchOne("Questionnaire", {
      identifier: `${Survey.Identifier.Vibrant}|${slug}`,
    });
    return questionnaire ? new Survey(questionnaire) : undefined;
  }

  /**
   * Create a new survey in a practice from a template.
   * This method handles all the logic of copying a template questionnaire
   * and converting it into a practice-specific survey.
   *
   * @param medplum - The Medplum client.
   * @param templateId - The ID of the template questionnaire.
   * @param practiceId - The ID of the practice.
   * @param options - Optional configuration for the new survey.
   * @returns The newly created survey.
   */
  static async createFromTemplate(
    medplum: MedplumClient,
    { templateId, practiceId, title, status, createOptions }: CreateSurveyFromTemplateParams,
  ): Promise<Survey> {
    // Fetch the template questionnaire
    const template = await medplum.readResource("Questionnaire", templateId);

    // Prepare the survey data by extracting necessary fields from the template
    const surveyData: SurveyCreateRequest = {
      practiceId,
      title: title || template.title || "Untitled Survey",
      description: template.description,
      status: status || "draft",
      item: template.item ? JSON.parse(JSON.stringify(template.item)) : undefined, // Deep clone items
    };

    // Create the survey with additional template-specific extensions
    const resource = await Survey.createResource(
      medplum,
      {
        ...Survey.toFhirCreateObject(surveyData),
        extension: [
          // Preserve any extensions from the template except template-specific ones
          ...(template.extension?.filter((ext) => !ext.url.includes("template")) || []),
          // Add tracking for which template this was copied from
          {
            url: Survey.Extension.CopiedFromTemplateId,
            valueString: templateId,
          },
          // Indicate this was not started from blank
          {
            url: Survey.Extension.StartedBlank,
            valueBoolean: false,
          },
        ],
      },
      createOptions,
    );

    return new Survey(resource);
  }

  /**
   * Get a survey by reference.
   *
   * @param medplum - The Medplum client.
   * @param reference - The reference to the survey.
   * @returns The survey, or undefined if not found or since deleted.
   */
  static async getByReference(
    medplum: MedplumClient,
    reference: Reference<Questionnaire>,
  ): Promise<Survey | undefined> {
    try {
      const questionnaire = await medplum.readReference<Questionnaire>(reference);
      return questionnaire ? new Survey(questionnaire) : undefined;
    } catch (err) {
      if (isMedplumResourceMissingError(err)) {
        logger.warn("Survey.getByReference: referenced Questionnaire is missing or deleted", {
          reference: reference.reference,
        });
        return undefined;
      }
      throw err;
    }
  }

  /**
   * Updates an existing survey.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the survey.
   * @param data - The update data.
   * @returns The updated survey.
   */
  static async update(
    medplum: MedplumClient,
    id: string,
    data: Partial<SurveyUpdateRequest>,
  ): Promise<Survey> {
    const resource = await Survey.patchResource(medplum, {
      id,
      patch: Survey.toFhirUpdateObject(data),
    });
    return new Survey(resource);
  }

  /**
   * Subscribe to surveys based on a given query.
   * Enables real-time updates when surveys are created, updated, or deleted.
   *
   * @param medplum - The Medplum client
   * @param query - Search options including pagination, sorting, and filtering
   * @param callback - Function to call when a survey changes
   * @returns Subscription that can be unsubscribed from
   */
  static subscribe(
    medplum: MedplumClient,
    query: SurveySearchOptions,
    callback: SubscriptionCallback<Survey>,
  ) {
    return Survey.subscribeToResource(medplum, Survey.toFhirQuery(query), (resource, metadata) =>
      callback(new Survey(resource), metadata),
    );
  }

  /**
   * Converts the survey data to a FHIR search query format.
   *
   * @param data - The survey search data.
   * @returns The FHIR query object.
   */
  /**
   * Converts domain query to FHIR subscription-safe filter params (no pagination/sort modifiers).
   */
  static toFhirQuery(data: SurveySearchOptions) {
    const { practiceId, includeAi, filter } = data;

    const searchParams: any = {
      "context-type": `${Survey.UseContext.Survey}|Organization/${practiceId}`,
    };

    if (!includeAi) {
      searchParams["context-type:not"] = `${Survey.UseContext.Source}|ai`;
    }

    if (filter) {
      searchParams._filter = `(title co "${filter}" or description co "${filter}")`;
    }

    return searchParams;
  }

  /**
   * Builds a full FHIR search query including pagination and sort modifiers.
   */
  private static toFhirSearchQuery(data: SurveySearchOptions) {
    const { page = 1, size = 10, sort } = data;

    return {
      ...Survey.toFhirQuery(data),
      _total: "accurate",
      _count: size,
      _offset: (page - 1) * size,
      ...(sort && { _sort: sort }),
    };
  }

  private static buildFhirObject(
    data: SurveyCreateRequest | SurveyUpdateRequest,
    options: { isCreate: boolean },
  ): ResourceData<Questionnaire> | Partial<ResourceData<Questionnaire>> {
    // Build extensions array - uses NullableExtension to support deletion signaling
    const extensions: NullableExtension[] = [];

    if ("startedBlank" in data && data.startedBlank) {
      extensions.push({ url: Survey.Extension.StartedBlank, valueBoolean: true });
    }

    if ("tags" in data && data.tags !== undefined) {
      const tagsValue = tagsToString(data.tags);
      if (tagsValue !== undefined) {
        // Add/update tags with the normalized value
        extensions.push({
          url: Survey.Extension.Tags,
          valueString: tagsValue,
        });
      } else {
        // Explicitly signal removal by setting valueString to null.
        // patchExtensions preserves existing extensions when absent from the patch,
        // so we must include the extension with null to trigger deletion.
        extensions.push({
          url: Survey.Extension.Tags,
          valueString: null,
        });
      }
    }

    const fhirData: Partial<ResourceData<Questionnaire>> = {
      title: data.title,
      description: data.description,
      item: data.item,
      extension: extensions.length > 0 ? (extensions as typeof fhirData.extension) : undefined,
    };

    if (options.isCreate) {
      fhirData.status = data.status ?? "draft";
    } else if (data.status !== undefined) {
      fhirData.status = data.status;
    }

    // Add identifier if provided
    if ("identifier" in data && data.identifier) {
      fhirData.identifier = [
        {
          system: Survey.Identifier.Vibrant,
          value: data.identifier,
        },
      ];
    }

    // Add practice reference and optional source if creating new survey
    if (options.isCreate && "practiceId" in data && data.practiceId) {
      const useContext: NonNullable<ResourceData<Questionnaire>["useContext"]> = [
        {
          code: {
            system: Survey.UseContext.Survey,
            code: `Organization/${data.practiceId}`,
            display: "Survey",
          },
          valueReference: {
            reference: `Organization/${data.practiceId}`,
          },
        },
      ];

      // If a source is provided (e.g., "ai"), include it as an additional context
      if ("source" in data && data.source === "ai") {
        useContext.push({
          code: { system: Survey.UseContext.Source, code: "ai", display: "AI" },
          valueCodeableConcept: {
            coding: [
              {
                system: Survey.UseContext.Source,
                code: "ai",
                display: "AI",
              },
            ],
            text: "AI",
          },
        });
      }

      fhirData.useContext = useContext;
    }

    return fhirData;
  }

  /**
   * Converts create request data into a FHIR Questionnaire payload.
   */
  static toFhirCreateObject(data: SurveyCreateRequest): ResourceData<Questionnaire> {
    return Survey.buildFhirObject(data, { isCreate: true }) as ResourceData<Questionnaire>;
  }

  /**
   * Converts update request data into a partial FHIR Questionnaire patch payload.
   */
  static toFhirUpdateObject(data: SurveyUpdateRequest): Partial<ResourceData<Questionnaire>> {
    return Survey.buildFhirObject(data, { isCreate: false }) as Partial<
      ResourceData<Questionnaire>
    >;
  }

  /**
   * Backward-compatible converter. Prefer `toFhirCreateObject`/`toFhirUpdateObject`.
   */
  static toFhirObject(data: SurveyCreateRequest): ResourceData<Questionnaire>;
  static toFhirObject(data: SurveyUpdateRequest): Partial<ResourceData<Questionnaire>>;
  static toFhirObject(
    data: SurveyCreateRequest | SurveyUpdateRequest,
  ): ResourceData<Questionnaire> | Partial<ResourceData<Questionnaire>> {
    return "practiceId" in data ? Survey.toFhirCreateObject(data) : Survey.toFhirUpdateObject(data);
  }

  //------------------------------------------------------------------------------------------------
  //                                     INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Add a question to this survey
   */
  addQuestion(question: Question): Survey {
    if (!this.resource.item) {
      this.resource.item = [];
    }
    this.resource.item.push(question.serialize());
    return this;
  }

  /**
   * Remove a question by id
   */
  removeQuestion(questionId: string): Survey {
    if (this.resource.item) {
      this.resource.item = this.resource.item.filter((item) => item.linkId !== questionId);
    }
    return this;
  }

  /**
   * Get a question by id
   */
  getQuestion(questionId: string): Question | undefined {
    if (!this.resource.item) return undefined;
    const item = this.resource.item.find((item) => item.linkId === questionId);
    return item ? Question.fromFHIR(item) : undefined;
  }

  /**
   * Update a question
   */
  updateQuestion(questionId: string, updater: (question: Question) => void): Survey {
    if (!this.resource.item) return this;

    const index = this.resource.item.findIndex((item) => item.linkId === questionId);
    if (index !== -1) {
      const question = new Question(this.resource.item[index]);
      updater(question);
      this.resource.item[index] = question.serialize();
    }

    return this;
  }

  /**
   * Find all questions matching a predicate (recursive search)
   */
  findQuestions(predicate: (question: Question) => boolean): Question[] {
    const results: Question[] = [];

    function searchItems(items: QuestionnaireItem[]) {
      for (const item of items) {
        const question = Question.fromFHIR(item);
        if (predicate(question)) {
          results.push(question);
        }
        if (item.item) {
          searchItems(item.item);
        }
      }
    }

    if (this.resource.item) {
      searchItems(this.resource.item);
    }

    return results;
  }

  /**
   * Count all questions (including nested ones)
   */
  getTotalQuestionCount(): number {
    let count = 0;

    function countItems(items: QuestionnaireItem[]) {
      count += items.length;
      for (const item of items) {
        if (item.item) {
          countItems(item.item);
        }
      }
    }

    if (this.resource.item) {
      countItems(this.resource.item);
    }

    return count;
  }

  async publish(medplum: MedplumClient): Promise<Survey> {
    const updatedSurvey = await Survey.update(medplum, this.id, { status: "active" });
    return updatedSurvey;
  }

  async unpublish(medplum: MedplumClient): Promise<Survey> {
    const updatedSurvey = await Survey.update(medplum, this.id, { status: "draft" });
    return updatedSurvey;
  }

  async delete(medplum: MedplumClient): Promise<void> {
    await Survey.delete(medplum, this.id);
  }

  /**
   * Recursively finds an item by linkId
   */
  private findItemRecursive(
    items: QuestionnaireItem[],
    linkId: string,
  ): QuestionnaireItem | undefined {
    for (const item of items) {
      if (item.linkId === linkId) {
        return item;
      }
      if (item.item) {
        const found = this.findItemRecursive(item.item, linkId);
        if (found) return found;
      }
    }
    return undefined;
  }

  /**
   * Recursively filters items based on a predicate
   */
  private filterItemsRecursive(
    items: QuestionnaireItem[],
    predicate: (item: QuestionnaireItem) => boolean,
  ): QuestionnaireItem[] {
    const results: QuestionnaireItem[] = [];

    for (const item of items) {
      if (predicate(item)) {
        results.push(item);
      }
      if (item.item) {
        results.push(...this.filterItemsRecursive(item.item, predicate));
      }
    }

    return results;
  }
}
